import Menu from '@mui/material/Menu';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { Theme } from '@mui/material/styles/createTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNativeBridge } from 'src/contexts/NativeBridgeContext';

import { Box, BoxProps } from './Box';

interface DropdownMenuProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  open?: boolean;
  closeOnContentClick?: boolean;
  sx?: BoxProps['sx'];
  drawerOnMobile?: boolean;
  setOpen?: (open: boolean) => void;
  disabled?: boolean;
  anchorOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  transformOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
}

export const useDropdownMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  return {
    isOpen,
    setIsOpen,
    anchorRef,
  };
};

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  trigger,
  sx,
  open: controlledOpen,
  setOpen: controlledSetOpen,
  children,
  drawerOnMobile,
  closeOnContentClick = true,
  disabled = false,
  anchorOrigin,
  transformOrigin,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { inset } = useNativeBridge();

  const isControlled =
    controlledOpen !== undefined && controlledSetOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const setIsOpen = useMemo(
    () =>
      isControlled
        ? (value: boolean | ((prevState: boolean) => boolean)) =>
            controlledSetOpen(
              typeof value === 'function' ? value(controlledOpen) : value
            )
        : setInternalOpen,
    [isControlled, controlledSetOpen, controlledOpen, setInternalOpen]
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const handleToggle = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  }, [setIsOpen, disabled]);

  return (
    <>
      <Box
        ref={anchorRef}
        onClick={handleToggle}
        sx={[
          {
            display: 'inline-block',
            cursor: disabled ? 'default' : 'pointer',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {trigger}
      </Box>
      {drawerOnMobile && smDown ? (
        <SwipeableDrawer
          disableSwipeToOpen
          id="basic-menu"
          open={isOpen}
          anchor="bottom"
          onClose={handleClose}
          onClick={() => closeOnContentClick && handleClose()}
          onOpen={() => setIsOpen(true)}
          sx={{
            '& .MuiDrawer-paper': {
              borderTopLeftRadius: '18px',
              borderTopRightRadius: '18px',
              maxHeight: `calc(100% - 24px - ${inset.top}px)`,
              pb: 3,
            },
          }}
        >
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              bgcolor: 'background.paper',
              borderTopLeftRadius: '18px',
              borderTopRightRadius: '18px',
              pt: 2,
              pb: 1,
            }}
          >
            <Box
              sx={{
                width: '50px',
                height: '7px',
                borderRadius: 2,
                bgcolor: 'divider',
                mx: 'auto',
              }}
            />
          </Box>
          {children}
        </SwipeableDrawer>
      ) : (
        <Menu
          id="basic-menu"
          anchorEl={anchorRef.current}
          open={isOpen}
          onClose={handleClose}
          sx={{ mt: 1 }}
          onClick={() => closeOnContentClick && handleClose()}
          MenuListProps={{ sx: { py: 0, px: 0 } }}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
        >
          <Box>{children}</Box>
        </Menu>
      )}
    </>
  );
};
